import { ClientApi } from '@quadminds/client-apis/base';
import { axiosResolver } from '@quadminds/shared/utils';
import { AxiosError, AxiosProgressEvent, AxiosRequestConfig } from 'axios';
import { AGENTS_API_PATHS } from './api-paths';
import { AGENTS_API_TOKEN_NAME, DefaultAgentsApiConfig } from './constants';
import { AgentsApiConfig } from './interfaces';
import {
    AgentAPIResponse,
    BulkUserEntry,
    BulkUsersResponse,
    Conversation,
    CreateFolderPayload,
    Document,
    FileInput,
    FileOutput,
    Folder,
    FolderStatistics,
    GoogleAuthResponse,
    GoogleCallbackResponse,
    ImportDocumentFromGooglePayload,
    ImportDocumentFromUriPayload,
    MessageOutput,
    MoveDocumentPayload,
    MoveDocumentResponse,
    Paginated,
    RateResponsePayload,
    SetPermissionRequest,
    SignedUrlResponse,
    UpdateFolderPayload,
    UploadProgressEvent,
    YpfImportResult,
    GeneralFeedback,
} from './types';

/**
 * Client-side Agents API for the QuadMinds platform.
 *
 * Provides a comprehensive interface for:
 * - Knowledge management and RAG-based messaging
 * - Conversation lifecycle management
 * - Document and folder operations with pagination
 * - File uploads with progress tracking
 * - Google Drive integration
 * - YPF template operations
 * - Logistics query processing
 *
 * Features:
 * - Automatic authentication token management
 * - Built-in retry logic for 401 responses
 * - Type-safe API responses
 * - Progress callbacks for file operations
 * - Centralized error handling
 */
export class AgentsApi extends ClientApi {
    private retryAttempts = 0;
    private readonly maxRetries = 3;

    constructor({ useUnauthorizedInterceptor = true, ...rest }: AgentsApiConfig = DefaultAgentsApiConfig) {
        super({
            namespace: AGENTS_API_TOKEN_NAME,
            ...rest,
        });

        if (useUnauthorizedInterceptor) {
            this.addAuthInterceptors();
        }
    }

    // === Knowledge and Chat Methods ===

    /**
     * Send a knowledge message using RAG (Retrieval Augmented Generation)
     */
    public async sendKnowledgeMessage(message: MessageOutput): Promise<AgentAPIResponse> {
        return axiosResolver<AgentAPIResponse>(() =>
            this.httpClient.post(AGENTS_API_PATHS.KNOWLEDGE.RAG, message, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Send a knowledge message using the v2 knowledge endpoint.
     */
    public async sendKnowledgeMessageV2(message: MessageOutput): Promise<AgentAPIResponse> {
        return axiosResolver<AgentAPIResponse>(() =>
            this.httpClient.post(AGENTS_API_PATHS.KNOWLEDGE.RAG_V2, message, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Send a logistics-specific message
     */
    public async sendLogisticsMessage(message: MessageOutput): Promise<AgentAPIResponse> {
        return axiosResolver<AgentAPIResponse>(() => {
            return this.httpClient.post(AGENTS_API_PATHS.LOGISTICS(message.mindy_version), message, {
                headers: { ...this.getAuthHeaders() },
            });
        });
    }

    // === Conversation Management Methods ===

    /**
     * Get all conversations
     */
    public async getConversations(page: number, pageSize: number): Promise<Conversation[]> {
        return axiosResolver<Conversation[]>(() =>
            this.httpClient.get(AGENTS_API_PATHS.CONVERSATIONS.ALL(page, pageSize), {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Get conversation by ID
     */
    public async getConversationById(id: string): Promise<Conversation> {
        return axiosResolver<Conversation>(() =>
            this.httpClient.get(AGENTS_API_PATHS.CONVERSATIONS.BY(id), {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Update conversation title and metadata
     */
    public async updateConversation(conversationId: string, payload: Partial<Conversation>): Promise<Conversation> {
        return axiosResolver<Conversation>(() =>
            this.httpClient.patch(AGENTS_API_PATHS.CONVERSATIONS.BY(conversationId), payload, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Delete conversation
     */
    public async deleteConversation(conversationId: string): Promise<void> {
        return axiosResolver<void>(() =>
            this.httpClient.delete(AGENTS_API_PATHS.CONVERSATIONS.BY(conversationId), {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Rate a conversation response
     */
    public async rateResponse(
        conversationId: string,
        interactionId: string,
        payload: RateResponsePayload,
    ): Promise<unknown> {
        return axiosResolver(() =>
            this.httpClient.patch(AGENTS_API_PATHS.CONVERSATIONS.RATE(conversationId, interactionId), payload, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    // === File Upload Methods ===

    /**
     * Upload files for ephemeral conversation use
     */
    public async uploadFile(files: File[], conversationId?: string): Promise<FileInput> {
        const formData = new FormData();
        formData.append('parser_service', 'langchain');
        if (conversationId) {
            formData.append('conversation_id', conversationId);
        }
        files.forEach((file) => formData.append('file', file));

        return axiosResolver<FileInput>(() =>
            this.httpClient.post(AGENTS_API_PATHS.KNOWLEDGE.UPLOAD, formData, {
                headers: {
                    ...this.getAuthHeaders(),
                    'Content-Type': 'multipart/form-data',
                },
            }),
        );
    }

    // === Document Management Methods ===

    /**
     * Get knowledge documents with pagination and filtering
     */
    public async getKnowledgeDocuments(
        page: number,
        pageSize: number,
        organizationId: string,
        additionalParams?: Record<string, unknown>,
    ): Promise<Paginated<Document, Folder>> {
        const params = new URLSearchParams({
            page: page.toString(),
            page_size: pageSize.toString(),
            organization_id: organizationId,
        });

        // Add individual filter parameters as expected by the API DTO
        if (additionalParams) {
            Object.entries(additionalParams).forEach(([key, value]) => {
                if (
                    value !== undefined &&
                    value !== null &&
                    value !== '' &&
                    key !== 'page' &&
                    key !== 'page_size' &&
                    key !== 'organization_id'
                ) {
                    if (Array.isArray(value)) {
                        // Handle array parameters (like tags)
                        value.forEach((item) => params.append(key, String(item)));
                    } else if (typeof value === 'boolean') {
                        params.append(key, value.toString());
                    } else {
                        params.append(key, String(value));
                    }
                }
            });
        }

        return axiosResolver<Paginated<Document, Folder>>(() =>
            this.httpClient.get(`${AGENTS_API_PATHS.DOCUMENTS.SEARCH_BY_FOLDER}?${params.toString()}`, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Upload knowledge file with progress tracking
     */
    public async uploadKnowledgeFile(
        file: File,
        organizationId: string,
        handleProgressEvent?: (event: UploadProgressEvent) => void,
        parentId?: string,
    ): Promise<FileOutput> {
        const formData = new FormData();
        formData.append('parser_service', 'langchain');
        formData.append('organization_id', organizationId);
        formData.append('file', file);
        if (parentId) {
            formData.append('parent_id', parentId);
        }

        return axiosResolver<FileOutput>(() =>
            this.httpClient.post(AGENTS_API_PATHS.DOCUMENTS.UPLOAD, formData, {
                headers: {
                    ...this.getAuthHeaders(),
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress(progressEvent: AxiosProgressEvent) {
                    handleProgressEvent && handleProgressEvent({ ...progressEvent, name: file.name });
                },
            }),
        );
    }

    /**
     * Update knowledge document
     */
    public async updateKnowledgeDocument(
        documentId: string,
        organizationId: string,
        payload: Partial<Document>,
    ): Promise<Document> {
        return axiosResolver<Document>(() =>
            this.httpClient.put(AGENTS_API_PATHS.DOCUMENTS.BY_FOLDER(organizationId, documentId), payload, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Delete knowledge document
     */
    public async deleteKnowledgeDocument(documentId: string, organizationId: string): Promise<unknown> {
        return axiosResolver(() =>
            this.httpClient.delete(
                `${AGENTS_API_PATHS.DOCUMENTS.ROOT}/${documentId}?organization_id=${organizationId}`,
                {
                    headers: { ...this.getAuthHeaders() },
                },
            ),
        );
    }

    /**
     * Get signed document URL
     */
    public async getSignedDocumentUrl(documentId: string): Promise<SignedUrlResponse> {
        return axiosResolver<SignedUrlResponse>(() =>
            this.httpClient.get(AGENTS_API_PATHS.DOCUMENTS.SIGNED_URL(documentId), {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    // === Folder Management Methods ===

    /**
     * Create folder
     */
    public async createFolder(payload: CreateFolderPayload): Promise<Folder> {
        return axiosResolver<Folder>(() =>
            this.httpClient.post(AGENTS_API_PATHS.FOLDERS.ROOT, payload, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Update folder
     */
    public async updateFolder(folderId: string, organizationId: string, payload: UpdateFolderPayload): Promise<Folder> {
        return axiosResolver<Folder>(() =>
            this.httpClient.put(
                `${AGENTS_API_PATHS.FOLDERS.ROOT}/${folderId}?organization_id=${organizationId}`,
                payload,
                {
                    headers: { ...this.getAuthHeaders() },
                },
            ),
        );
    }

    /**
     * Delete folder
     */
    public async deleteFolder(folderId: string, organizationId: string, recursive = false): Promise<void> {
        return axiosResolver<void>(() =>
            this.httpClient.delete(
                `${AGENTS_API_PATHS.FOLDERS.ROOT}/${folderId}?organization_id=${organizationId}&recursive=${recursive}`,
                {
                    headers: { ...this.getAuthHeaders() },
                },
            ),
        );
    }

    /**
     * Move document to folder
     */
    public async moveDocumentToFolder(documentId: string, payload: MoveDocumentPayload): Promise<MoveDocumentResponse> {
        return axiosResolver<MoveDocumentResponse>(() =>
            this.httpClient.post(`${AGENTS_API_PATHS.DOCUMENTS.ROOT}/${documentId}/move`, payload, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Get folder statistics
     */
    public async getFolderStatistics(folderId?: string): Promise<FolderStatistics> {
        const params = folderId ? { folder_id: folderId } : {};
        return axiosResolver<FolderStatistics>(() =>
            this.httpClient.get(AGENTS_API_PATHS.DOCUMENTS.STATS, {
                headers: { ...this.getAuthHeaders() },
                params,
            }),
        );
    }

    // === Google Integration Methods ===

    /**
     * Redirect to Google Auth
     */
    public async redirectToGoogleAuth(redirectUrl: string): Promise<GoogleAuthResponse> {
        return axiosResolver<GoogleAuthResponse>(() =>
            this.httpClient.get(AGENTS_API_PATHS.AUTH.GOOGLE, {
                headers: { ...this.getAuthHeaders() },
                params: { redirect_url: redirectUrl },
            }),
        );
    }

    /**
     * Resolve Google Auth Code
     */
    public async resolveGoogleAuthCode(code: string, state: string): Promise<GoogleCallbackResponse> {
        return axiosResolver<GoogleCallbackResponse>(() =>
            this.httpClient.post(
                AGENTS_API_PATHS.AUTH.CALLBACK,
                { code, state },
                { headers: { ...this.getAuthHeaders() } },
            ),
        );
    }

    /**
     * Import documents from Google Drive
     */
    public async importDocumentsFromGoogle(
        payload: ImportDocumentFromGooglePayload,
        handleProgressEvent?: (event: UploadProgressEvent) => void,
    ): Promise<FileOutput> {
        const googleAccessToken = localStorage.getItem('googleAccessToken');
        if (!googleAccessToken) {
            throw new Error('Google access token is not available');
        }

        return axiosResolver<FileOutput>(() =>
            this.httpClient.post(`/${AGENTS_API_PATHS.DOCUMENTS.IMPORT}`, payload, {
                headers: {
                    ...this.getAuthHeaders(),
                    'x-issued-google-token': googleAccessToken,
                },
                onUploadProgress(progressEvent: AxiosProgressEvent) {
                    handleProgressEvent && handleProgressEvent({ ...progressEvent, name: payload.file_name });
                },
            }),
        );
    }

    /**
     * Import documents from URI
     */
    public async importDocumentsFromUri(
        payload: ImportDocumentFromUriPayload,
        handleProgressEvent?: (event: UploadProgressEvent) => void,
    ): Promise<FileOutput> {
        return axiosResolver<FileOutput>(() =>
            this.httpClient.post(`${AGENTS_API_PATHS.DOCUMENTS.IMPORT_BY_URI}`, payload, {
                headers: { ...this.getAuthHeaders() },
                onUploadProgress(progressEvent: AxiosProgressEvent) {
                    handleProgressEvent && handleProgressEvent({ ...progressEvent, name: payload.url });
                },
            }),
        );
    }

    // === YPF Methods ===

    /**
     * Upload YPF file
     */
    public async uploadYpfFile(
        file: File,
        handleProgressEvent?: (event: UploadProgressEvent) => void,
    ): Promise<YpfImportResult> {
        const formData = new FormData();
        formData.append('file', file);

        return axiosResolver<YpfImportResult>(() =>
            this.httpClient.post(AGENTS_API_PATHS.DOCUMENTS.YPF_IMPORT, formData, {
                headers: {
                    ...this.getAuthHeaders(),
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress(progressEvent: AxiosProgressEvent) {
                    handleProgressEvent && handleProgressEvent({ ...progressEvent, name: file.name });
                },
            }),
        );
    }

    /**
     * Get YPF template
     */
    public async getYpfTemplate(): Promise<Blob> {
        return axiosResolver<Blob>(() =>
            this.httpClient.get(AGENTS_API_PATHS.DOCUMENTS.YPF_TEMPLATE, {
                headers: { ...this.getAuthHeaders() },
                responseType: 'blob',
            }),
        );
    }

    // === Utility Methods ===

    /**
     * Get authentication headers with current bearer token
     */
    private getAuthHeaders() {
        const token = this.getCurrentToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    }

    /**
     * Retrieve current authentication token from localStorage
     */
    private getCurrentToken(): string | null {
        return localStorage.getItem('token');
    }

    /**
     * Add authentication interceptor for 401 responses.
     * Delegates token refresh handling to consuming applications
     * to maintain separation of concerns.
     */
    private addAuthInterceptors() {
        this.httpClient.interceptors.response.use(
            (response) => response,
            async (error: AxiosError & { response?: { data: { message: string } } }) => {
                const originalRequest = error.config as AxiosRequestConfig & { isRetry?: boolean };

                if (
                    error.response?.status === 401 &&
                    error.response?.data?.message &&
                    error.response?.data?.message !== 'API Key not provided' &&
                    !originalRequest.isRetry &&
                    this.retryAttempts < this.maxRetries
                ) {
                    // Mark as retry to prevent infinite loops
                    originalRequest.isRetry = true;
                    this.retryAttempts++;

                    // Delegate token refresh to consuming applications
                    // Applications should implement their own interceptors for token refresh
                    return Promise.reject(error);
                }

                return Promise.reject(error);
            },
        );
    }
    public async getAllPermissions() {
        const { data } = await this.httpClient.get(AGENTS_API_PATHS.PERMISSIONS.ROOT, {
            headers: { ...this.getAuthHeaders() },
        });
        return data;
    }

    public async updateDatePermissions(setPermissionRequest: SetPermissionRequest) {
        const { data } = await this.httpClient.patch(
            AGENTS_API_PATHS.PERMISSIONS.ADD_BULK_PERMISSIONS(setPermissionRequest.document_id),
            setPermissionRequest,
            {
                headers: { ...this.getAuthHeaders() },
            },
        );
        return data;
    }

    public async getUsersPerDocument(documentId: string) {
        const { data } = await this.httpClient.get(AGENTS_API_PATHS.PERMISSIONS.DOCUMENT_PERMISSIONS(documentId), {
            headers: { ...this.getAuthHeaders() },
        });
        return data;
    }

    public async userIsMindyAdminChange(userId: string) {
        const { data } = await this.httpClient.delete(AGENTS_API_PATHS.DOCUMENTS.USERS(userId), {
            headers: { ...this.getAuthHeaders() },
        });
        return data;
    }

    public async getAllQuestions() {
        const { data } = await this.httpClient.get(AGENTS_API_PATHS.QUESTIONS.ALL, {
            headers: { ...this.getAuthHeaders() },
        });
        return data;
    }

    public async createGeneralFeedback(generalFeedback: GeneralFeedback): Promise<GeneralFeedback> {
        const { data } = await this.httpClient.post(AGENTS_API_PATHS.FEEDBACK.GENERAL_FEEDBACK, generalFeedback, {
            headers: { ...this.getAuthHeaders() },
        });
        return data;
    }

    public async replaceFile(documentId: string, file: File) {
        const formData = new FormData();
        formData.append('file', file);
        return this.httpClient.put(AGENTS_API_PATHS.DOCUMENTS.REPLACE(documentId), formData, {
            headers: {
                ...this.getAuthHeaders(),
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    public async getDocumentById(documentId: string): Promise<Document> {
        const { data } = await this.httpClient.get(AGENTS_API_PATHS.DOCUMENTS.BY_ID(documentId), {
            headers: { ...this.getAuthHeaders() },
        });
        return data;
    }

    /**
     * Add multiple users to an organization in bulk
     */
    public async addOrganizationUsersBulk(organizationId: string, users: BulkUserEntry[]): Promise<BulkUsersResponse> {
        const { data } = await this.httpClient.post<BulkUsersResponse>(
            AGENTS_API_PATHS.ORGANIZATIONS.USERS_BULK(organizationId),
            { users },
            { headers: { ...this.getAuthHeaders() } },
        );
        return data;
    }

    /**
     * Delete multiple users from an organization in bulk
     */
    public async deleteOrganizationUsersBulk(
        organizationId: string,
        users: Pick<BulkUserEntry, 'user_id'>[],
    ): Promise<BulkUsersResponse> {
        const { data } = await this.httpClient.delete<BulkUsersResponse>(
            AGENTS_API_PATHS.ORGANIZATIONS.USERS_BULK(organizationId),
            {
                data: { users },
                headers: { ...this.getAuthHeaders() },
            },
        );
        return data;
    }
}
